import { UserPlus, Users as UsersIcon } from "lucide-react";
import { useState } from "react";
import { Button, cx, Empty, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, APP_NAMES, APP_ROLES, ROLE_LABELS, roleList, useApi, type AppId, type Me } from "../lib/api";

interface UserRow {
  email: string;
  name?: string;
  status: "invited" | "active" | "disabled";
  tenantAdmin: boolean;
  entitlements: Partial<Record<AppId, { roles: string[]; connections?: string[] }>>;
}

/** Tenant admin: invite users, set app roles, admin flag, disable (design §4.3). */
export default function UsersPage({ me }: { me: Me }) {
  const { data, error, loading, reload } = useApi<{ users: UserRow[] }>("/admin/users");
  const [editing, setEditing] = useState<UserRow | "new" | null>(null);
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Users</h1>
          <p className="text-sm text-muted">{me.tenant!.signInMode === "federated" ? "People you add here can sign in with your organization's single sign-on." : "People you add get an email invitation from the portal."}</p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          <UserPlus className="size-4" aria-hidden /> Add user
        </Button>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data?.users.length === 0 ? (
        <Empty icon={<UsersIcon className="size-8" />} title="No users yet" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">User</th>
                <th className="px-4 py-2 font-medium">Apps</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data?.users.map((u) => (
                <tr key={u.email} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink">
                      {u.name || u.email} {u.tenantAdmin && <span className="ml-1 text-xs rounded bg-accent-chip text-accent-text px-1.5 py-0.5">Admin</span>}
                    </div>
                    {u.name && <div className="text-xs text-muted">{u.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-2">
                    {(Object.entries(u.entitlements) as [AppId, { roles: string[] }][]).map(([a, e]) => (e.roles[0] === "user" ? APP_NAMES[a] : `${APP_NAMES[a]} (${roleList(e.roles)})`)).join("; ") || "None"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={u.status === "active" ? "text-good" : u.status === "invited" ? "text-warn" : "text-bad"}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" onClick={() => setEditing(u)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <UserForm me={me} user={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />}
    </div>
  );
}

function UserForm({ me, user, onClose, onSaved }: { me: Me; user?: UserRow; onClose: () => void; onSaved: () => void }) {
  const licensed = me.tenant!.apps;
  const [email, setEmail] = useState(user?.email ?? "");
  const [name, setName] = useState(user?.name ?? "");
  const [tenantAdmin, setTenantAdmin] = useState(user?.tenantAdmin ?? false);
  const [roles, setRoles] = useState<Partial<Record<AppId, string[]>>>(Object.fromEntries(Object.entries(user?.entitlements ?? {}).map(([a, e]) => [a, e!.roles])));
  const toggle = (a: AppId, r: string, on: boolean) => setRoles((cur) => ({ ...cur, [a]: on ? [...(cur[a] ?? []), r] : (cur[a] ?? []).filter((x) => x !== r) }));
  // Per-app connection limits (design §4.3): undefined = every connection.
  const connList = useApi<{ connections: { connId: string; name: string; envLabel: string; production: boolean }[] }>("/admin/connections").data?.connections ?? [];
  const [limits, setLimits] = useState<Partial<Record<AppId, string[] | undefined>>>(Object.fromEntries(Object.entries(user?.entitlements ?? {}).map(([a, e]) => [a, e!.connections])));
  const toggleLimit = (a: AppId, connId: string, on: boolean) =>
    setLimits((cur) => {
      const now = cur[a] ?? connList.map((c) => c.connId);
      const next = on ? [...new Set([...now, connId])] : now.filter((x) => x !== connId);
      return { ...cur, [a]: next.length === connList.length ? undefined : next };
    });
  const [disabled, setDisabled] = useState(user?.status === "disabled");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const self = user?.email === me.email;

  const save = async () => {
    setBusy(true);
    setError(null);
    // Keep any connection limits already on an entitlement; connection-scoped roles arrive with Infor connections.
    const entitlements = Object.fromEntries(
      Object.entries(roles)
        .filter(([, r]) => r && r.length)
        .map(([a, r]) => [a, { roles: r, ...(limits[a as AppId] ? { connections: limits[a as AppId] } : {}) }]),
    );
    try {
      if (user) await api(`/admin/users/${encodeURIComponent(user.email)}`, { method: "PUT", body: { name, tenantAdmin, entitlements, ...(self ? {} : { status: disabled ? "disabled" : "active" }) } });
      else await api("/admin/users", { body: { email, name, tenantAdmin, entitlements } });
      toast(user ? "User saved" : "User added");
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title={user ? `Edit ${user.name || user.email}` : "Add user"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {user ? "Save" : "Add user"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {!user && (
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Email</span>
            <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        )}
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <fieldset className="text-sm space-y-2">
          <legend className="text-ink-2 font-medium mb-1">Apps</legend>
          <p className="text-xs text-muted">Tick what each person may use; for Job Automation, each box is a screen. Apps {me.tenant!.name} isn't licensed for are greyed out; Intellias licenses them.</p>
          {(Object.keys(APP_NAMES) as AppId[]).map((a) => (
            <div key={a} className={cx("flex flex-wrap items-start justify-between gap-x-3 gap-y-1", !licensed.includes(a) && "opacity-50")}>
              <span>
                {APP_NAMES[a]}
                {!licensed.includes(a) && <span className="ml-2 text-xs text-muted">Not licensed</span>}
              </span>
              <span className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                {APP_ROLES[a].map((r) => (
                  <label key={r} className="inline-flex items-center gap-1.5">
                    <input type="checkbox" aria-label={`${APP_NAMES[a]}: ${ROLE_LABELS[r]}`} disabled={!licensed.includes(a)} checked={(roles[a] ?? []).includes(r)} onChange={(e) => toggle(a, r, e.target.checked)} />
                    {ROLE_LABELS[r]}
                  </label>
                ))}
              </span>
              {licensed.includes(a) && (roles[a] ?? []).length > 0 && connList.length > 0 && (
                <span className="basis-full flex flex-wrap items-center gap-x-3 gap-y-1 pl-4 text-xs text-muted">
                  <span>Connections:</span>
                  {connList.map((c) => (
                    <label key={c.connId} className="inline-flex items-center gap-1">
                      <input type="checkbox" checked={!limits[a] || limits[a]!.includes(c.connId)} onChange={(e) => toggleLimit(a, c.connId, e.target.checked)} />
                      <span className={c.production ? "text-bad" : ""}>
                        {c.name} ({c.envLabel})
                      </span>
                    </label>
                  ))}
                  {!limits[a] && <span className="italic">all</span>}
                </span>
              )}
            </div>
          ))}
        </fieldset>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tenantAdmin} disabled={self} onChange={(e) => setTenantAdmin(e.target.checked)} />
          Tenant admin (manages users and set-up)
        </label>
        {user && !self && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />
            Disabled (can't sign in)
          </label>
        )}
      </div>
    </Modal>
  );
}
